import { lazy, Suspense } from "react";

import { ProtectedRoute } from "./guards/ProtectedRoute";
import AdminConsoleLayout from "../components/admin/AdminConsoleLayout";
import FallbackUI from "../components/fallback-ui/FallbackUI";

const AdminDashboard = lazy(() => import("../pages/admin/AdminDashboard"));
const DeveloperToolsPage = lazy(() => import("../pages/admin/DeveloperToolsPage"));
const OrganisationsPage = lazy(() => import("../pages/admin/OrganisationsPage"));
const ProvisionOrgWizard = lazy(() => import("../pages/admin/ProvisionOrgWizard"));
const ConfigEditorPage = lazy(() => import("../pages/admin/ConfigEditorPage"));
const UsersPage = lazy(() => import("../pages/admin/UsersPage"));
const RestrictionsPage = lazy(() => import("../pages/admin/RestrictionsPage"));
const ActivityPage = lazy(() => import("../pages/admin/ActivityPage"));
const ApiKeysPage = lazy(() => import("../pages/admin/ApiKeysPage"));

// Admin console route tree. Kept in its own module (importing only admin-related
// code) so it can be unit-tested with a memory router without pulling in the
// full app graph (MainLayout → PreviewMap → maplibre-gl).
export const adminRoutes = [
  {
    element: (
      // Entry needs a MANAGEMENT permission, not merely write access. Plain
      // editors used to get in and find a dead-end shell; now the tab is not
      // offered and the route rejects them. Staff and superusers still reach
      // Developer Tools and Organisations via their own route guards below.
      <ProtectedRoute
        requiredPermissions={["canManageUsers", "canManageConfiguration", "canManageRestrictions", "canBulkApiDataImport"]}
        allowStaff
      >
        <AdminConsoleLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: "admin",
        element: (
          <Suspense fallback={<FallbackUI />}>
            <AdminDashboard />
          </Suspense>
        )
      },
      {
        // Legacy Fulcrum config generators — staff/superuser only. The nav hides
        // this for everyone else, but the route needs its own guard or an editor
        // who types the path still lands inside the console shell (B-3).
        // Roles mirror adminNav's `isStaff: is_staff || is_superuser`.
        path: "admin/developer-tools",
        element: (
          <ProtectedRoute allowedRoles={["SUPER_ADMIN", "STAFF_USER"]}>
            <Suspense fallback={<FallbackUI />}>
              <DeveloperToolsPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        // Cross-tenant surfaces (list all orgs, spin up a new one) are superuser-only.
        // Org admins are bounced to their own org's config by the guard.
        path: "admin/organisations",
        element: (
          <ProtectedRoute requireSuperuser>
            <Suspense fallback={<FallbackUI />}>
              <OrganisationsPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        path: "admin/provisioning",
        element: (
          <ProtectedRoute requireSuperuser>
            <Suspense fallback={<FallbackUI />}>
              <ProvisionOrgWizard />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        // Organisation config authors branding, map layers, data sources, tables
        // and Authentication flags — tenant-shaping settings, not content edits.
        // Gated on manage_configuration, which also covers the branding upload
        // on this page. Editors are still excluded (B-2); the difference is that
        // an admin WITHOUT user management can now reach their own org settings.
        path: "admin/config",
        element: (
          <ProtectedRoute requiredPermissions={["canManageConfiguration"]}>
            <Suspense fallback={<FallbackUI />}>
              <ConfigEditorPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        // Org-scoped user management: roles, restriction assignment, invite.
        // manage_users only — editors/staff who reach the console shell are excluded.
        path: "admin/users",
        element: (
          <ProtectedRoute requiredPermissions={["canManageUsers"]}>
            <Suspense fallback={<FallbackUI />}>
              <UsersPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        // Org-scoped restriction definitions (create/edit/delete). Coarse:
        // holding manage_restrictions is full access, with no extra CRUD layered on.
        path: "admin/restrictions",
        element: (
          <ProtectedRoute requiredPermissions={["canManageRestrictions"]}>
            <Suspense fallback={<FallbackUI />}>
              <RestrictionsPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        // Org-scoped API keys: mint, rotate, revoke. Configuration, not user
        // management — a key belongs to the organisation (GeoStyler review §17.8).
        path: "admin/api-keys",
        element: (
          <ProtectedRoute requiredPermissions={["canManageConfiguration"]}>
            <Suspense fallback={<FallbackUI />}>
              <ApiKeysPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        // Org-scoped "who's using it" activity summary. manage_users only.
        path: "admin/activity",
        element: (
          <ProtectedRoute requiredPermissions={["canManageUsers"]}>
            <Suspense fallback={<FallbackUI />}>
              <ActivityPage />
            </Suspense>
          </ProtectedRoute>
        )
      }
    ]
  }
];
